import React, { ReactNode, useMemo } from 'react'
import RmdTextField, { TextFieldProps as RmdTextFieldProps } from 'react-md/lib/TextFields/TextField'
import classnames from 'classnames'
import omit from 'lodash/omit'
import styled from 'styled-components'
import Box from './Box'
import CustomTooltip from 'shared/components/CustomTooltip'

interface TextFieldProps extends RmdTextFieldProps {
  icon?: string | ReactNode
  marginBottom?: string | number
  caution?: boolean
  isDefaultLabel?: boolean
  hasTooltip?: boolean
  tooltipIcon?: string | ReactNode
  tooltipMessage?: string
}

const TextField: React.FC<TextFieldProps> = (props: TextFieldProps) => {
  const ommitedProps = useMemo(() => {
    const omittedPropsArr = ['className', 'icon', 'required', 'marginBottom']
    if (!props.isDefaultLabel) {
      omittedPropsArr.push('label')
    }

    return omit(props, omittedPropsArr)
  }, [props])

  const inputField = <StyledTextField {...ommitedProps} $hasIcon={props.icon !== undefined} />

  if (props.icon) {
    return (
      <StyledTextFieldContainer
        className={classnames(props.className, { multiline: props.rows })}
        mb={props.marginBottom}
      >
        {props.label && !props.isDefaultLabel && (
          <FieldLabelContainer>
            <FieldLabel caution={props.caution}>{props.label}</FieldLabel>
            {props.hasTooltip && <CustomTooltip title={props.tooltipMessage || ''} />}
          </FieldLabelContainer>
        )}
        <Box>
          {inputField}
          <Icon>{typeof props.icon === 'string' ? <img src={props.icon} alt="" /> : props.icon}</Icon>
        </Box>
      </StyledTextFieldContainer>
    )
  } else {
    return (
      <StyledTextFieldContainer
        className={classnames(props.className, { multiline: props.rows })}
        mb={props.marginBottom}
      >
        {props.label && !props.isDefaultLabel && (
          <FieldLabelContainer>
            <FieldLabel caution={props.caution}>{props.label}</FieldLabel>
            {props.hasTooltip && <CustomTooltip title={props.tooltipMessage || ''} />}
          </FieldLabelContainer>
        )}
        {inputField}
      </StyledTextFieldContainer>
    )
  }
}

export const FieldLabel = styled(Box)<{ caution?: boolean }>`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  color: ${({ theme, caution }) => (caution ? theme.colors.caution : theme.colors.secondary)};
`

export const Icon = styled.div`
  display: flex;
  position: absolute;
  top: 25px;
  left: 14px;
  transform: translateY(-50%);
`

const FieldLabelContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`

export const StyledTextFieldContainer = styled(Box)`
  &.multiline {
    height: unset;
  }

  .md-text-field-multiline-container {
    width: 100%;

    &.md-text-field--margin {
      margin-top: 3px;
    }
  }
`

export const StyledTextField = styled(RmdTextField)<{ $hasIcon?: boolean; caution?: boolean }>`
  &.md-text-field-container {
    display: flex;
    align-items: center;
    flex-direction: column;

    .md-text-field {
      ${({ theme, $hasIcon, caution }) => `
        background: ${theme.colors.white};
        border: 1px solid  ${caution ? theme.colors.caution : theme.colors.inputBorder};
        padding: ${$hasIcon ? '4px 8px 4px 50px' : '4px 14px'};
      `}
      height: 50px;
      border-radius: 5px;
      margin-top: 0;
      font-size: 16px;

      &[type='password'] {
        width: 100%;
        padding-right: 45px;
      }

      &::-ms-reveal,
      &::-ms-clear {
        display: none;
      }

      &--inline-indicator {
        width: 100%;
      }

      &--multiline {
        padding: 8px 14px;
      }

      &:focus {
        ${({ theme, caution }) => `border: 1px solid  ${caution ? theme.colors.caution : theme.colors.main};`}
      }

      &::-webkit-scrollbar {
        width: 8px;
        background-color: #f5f5f5;
        border-radius: 5px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${({ theme }) => theme.colors.label};
        border-radius: 50px;
      }

      &:disabled {
        background: #dee1e1;
      }
    }

    .md-divider {
      display: none;
    }

    .md-password-btn {
      margin-right: 18px;
    }

    &--multiline {
      height: fit-content;
    }

    .md-password-btn {
      top: 25px;
      transform: translateY(-50%);
    }
  }
`

export default TextField
